import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cx from 'classnames';

import { TranslatorContext } from '@jutro/locale';
import { Avatar, Icon } from '@jutro/components';
import { ServiceManager } from '@jutro/services';

import { readViewModelValue } from 'gw-jutro-adapters-react';
import { withViewModelService, ViewModelForm } from 'gw-portals-viewmodel-react';
import { BreakpointTrackerContext, withBreakpoint } from '@jutro/layout';
import { PhoneUtil } from 'gw-portals-viewmodel-js';

import { ContactService } from 'gw-capability-gateway';
import { withAuthenticationContext } from 'gw-digital-auth-react';

import metadata from './PolicyContactsPage.metadata.json5';
import tabStyles from '../Contacts.module.scss';
import messages from '../Contacts.messages';
import gatewayMessages from '../../gateway.messages';
import {getMetadataByBreakPoint} from './../../../../utils/breakpointUtil';

const PRIMARY_PHONE_TYPE_MAP = {
    home: 'homeNumber',
    mobile: 'cellNumber',
    work: 'workNumber'
};

const roleAbstractEntities = [
    'PolicyContactRole',
    'BAPolicyContactRole',
    'BOPPolicyContactRole',
    'CPPolicyContactRole',
    'GLPolicyContactRole',
    'PAPolicyContactRole',
    'IMPolicyContactRole',
    'PolicyNamedInsured',
    'WCPolicyContactRole',
    'WCLaborContact',
    'PlcyNonPriNamedInsured',
    'PolicyNamedInsured'
];

class PolicyContactsPage extends Component {
    static contextType = TranslatorContext;

    state = {
        policyContactRoleVM: {},
        policyContactRole: 'allRoles',
        policyContactRoleOptions: [],
        policyContactTableData: {},
        data: [],
        previousContactRole: 'allRoles' 
    };

    static propTypes = {
        viewModelService: PropTypes.shape({
            create: PropTypes.func
        }).isRequired,
        history: PropTypes.shape({
            push: PropTypes.func
        }).isRequired,
        policyNumber: PropTypes.string.isRequired,
        authHeader: PropTypes.shape({}).isRequired
    };

    writeValue = (value) => {
        this.setState({
            policyContactRole: value
        });
    };

    createVM = () => {
        const { viewModelService } = this.props;
        return viewModelService.create(
            {},
            'pc',
            'edge.capabilities.gateway.contact.dto.roles.policy.PolicyContactRoleDTO'
        );
    };

    componentDidMount = () => {
        const policyContactRoleVM = this.createVM();

        this.setState({ policyContactRoleVM }, () => {
            const policyContactRoleOptions = this.getPolicyContactRoles();
            this.setState({ policyContactRoleOptions });
        });

        this.loadTableData();
    };

    getPolicyContactSummaries = (paginationParams) => {
        const { policyContactRole } = this.state;
        const { policyNumber, authHeader } = this.props;
        const role = policyContactRole !== 'allRoles' ? [policyContactRole] : [];

        return ContactService.getPolicyContactSummaries(
            policyNumber,
            authHeader,
            role,
            paginationParams.fromPage,
            paginationParams.pageSize
        ).then((response) => {
            this.setState({
                policyContactTableData: response
            });
            const data = this.getResponseData(response.policyContacts);
            return {
                rows: data,
                numberOfRows: response.maxNumberOfResults
            };
        });
    };

    getResponseData = (policyContacts) => {
        if (policyContacts) {
            const contactsInfo = policyContacts.map((contact) => {
                return {
                    contact: contact.displayName,
                    role: contact.policyContactRoles,
                    involvedWith: contact.associatedPoliciesCount,
                    active: contact.active
                };
            });
            return contactsInfo;
        }
        return [];
    };

    getPolicyContactRoles = () => {
        const { policyContactRoleVM } = this.state;
        const translator = this.context;
        const allRolesOption = {
            code: 'allRoles',
            name: messages.allRoles
        };

        const contactRoles = _.get(policyContactRoleVM, 'subtype.aspects.availableValues', []);

        const roleOptions = contactRoles
            .filter((option) => {
                return !roleAbstractEntities.includes(option.code);
            })
            .map((role) => {
                return {
                    code: role.code,
                    name: translator(messages[_.lowerFirst(role.code)])
                };
            });

        roleOptions.unshift(allRolesOption);
        return roleOptions;
    };

    redirectToContactDetails = (index) => {
        const { policyNumber, history } = this.props;
        const { policyContactTableData } = this.state;
        const { policyContacts } = policyContactTableData;

        history.push({
            pathname: `/policies/${policyNumber}/contactDetails`,
            state: {
                contact: 'policyContacts',
                accountOrPolicyNumber: policyNumber,
                relatedTo: 'policies',
                contactId: policyContacts[index].accountContactPublicID
            }
        });
    };

    renderContact = (item, index, { path: property }) => {
        const { policyContactTableData } = this.state;
        if (index < policyContactTableData.policyContacts.length) {
            const contact = policyContactTableData.policyContacts[index];
            let primaryPhoneNumber = '';

            const contactAvatarClass = cx(tabStyles.gwContactAvatar, {
                [tabStyles.gwActiveContactAvatar]: item.active,
                [tabStyles.gwInactiveContactAvatar]: !item.active
            });

            if (contact.primaryPhoneType) {
                const phoneNumber = contact[PRIMARY_PHONE_TYPE_MAP[contact.primaryPhoneType]];
                const localeService = ServiceManager.getService('locale-service');
                const phoneUtil = PhoneUtil();
                primaryPhoneNumber = phoneUtil.prettyPrint(
                    phoneNumber,
                    localeService.getDefaultCountryCode()
                );
            }

            const phoneIconClass = cx(tabStyles.gwContactPhoneIcon, 'fa-flip-horizontal');

            const primaryPhoneNumberContainer = primaryPhoneNumber && (
                <div className={tabStyles.gwContactPhoneNumber}>
                    <Icon
                        className={phoneIconClass}
                        icon="mi-phone"
                        onClick={() => {
                            this.redirectToContactDetails(index);
                        }}
                    />
                    {primaryPhoneNumber}
                </div>
            );

            const emailAddressContainer = contact.emailAddress1 && (
                <div className={tabStyles.gwContactEmail}>
                    <Icon
                        className={tabStyles.gwContactEmailIcon}
                        icon="mi-email"
                        onClick={() => {
                            this.redirectToContactDetails(index);
                        }}
                    />
                    {contact.emailAddress1}
                </div>
            );

            return (
                <div className={tabStyles.gwContactsTableWrapper}>
                    <div className={tabStyles.gwContactAvatarContainer}>
                        <Avatar
                            className={contactAvatarClass}
                            username={item[property]}
                            onClick={() => {
                                this.redirectToContactDetails(index);
                            }}
                        />
                    </div>
                    <div className={tabStyles.gwContactNameContainer}>
                        <div className={tabStyles.gwContactName}>{item[property]}</div>
                        <div className={tabStyles.gwContactPhoneNumberWrapper}>
                            {primaryPhoneNumberContainer}
                            {emailAddressContainer}
                        </div>
                    </div>
                </div>
            );
        }

        return <div />;
    };

    renderRole = (item, index, { path: property }) => {
        const { policyContactRoleVM } = this.state;
        const translator = this.context;
        const roles = item[property];
        const roleOptions = policyContactRoleVM.subtype.aspects.availableValues;
        return (
            <div>
                {roles.map((data) => {
                    const matchingRole = roleOptions.find((opt) => opt.code === data);
                    return matchingRole ? (
                        <div>{translator(messages[_.lowerFirst(matchingRole.code)])}</div>
                    ) : null;
                })}
            </div>
        );
    };

    renderPoliciesInvolved = (item, index, { path: property }) => {
        const policiesCount = item[property];
        const isActive = item.active;

        const policiesInvolved = isActive
            ? (
                <TranslatorContext.Consumer>
                    {(translator) => {
                        const activePolicies = policiesCount > 1
                            ? translator(messages.activePolicies)
                            : translator(messages.activePolicy);
                        return (
                            <>
                                <span className={tabStyles.gwInvolvedPolicies}>
                                    {policiesCount}
                                </span>
                                {activePolicies}
                            </>
                        );
                    }}
                </TranslatorContext.Consumer>
            )
            : (
                <TranslatorContext.Consumer>
                    {(translator) => {
                        return (
                            translator(gatewayMessages.inactive)
                        );
                    }}
                </TranslatorContext.Consumer>
            );

        return (
            <div className={tabStyles.gwInvolvedPoliciesContainer}>
                <span>{policiesInvolved}</span>
                <Icon
                    className={tabStyles.gwInvolvedPoliciesArrow}
                    icon="mi-keyboard-arrow-right"
                    onClick={() => {
                        this.redirectToContactDetails(index);
                    }}
                />
            </div>
        );
    };

    loadData = ({ pageSize, page }) => {
        const paginationParams = {
            fromPage: page,
            pageSize: pageSize
        };

        return this.getPolicyContactSummaries(paginationParams);
    };

    loadTableData = () => {
        this.loadData({pageSize:20, page: 0}).then((response) => {
            this.setState({data: response.rows})
        })
    }

    getBreakPoint = () => {        
        const breakpointContext = <BreakpointTrackerContext.Consumer />;
        const {
            type: { _currentValue: breakpoint }
        } = breakpointContext;
        return breakpoint
    };

    render() {
        const { policyContactRole, policyContactRoleOptions, previousContactRole } = this.state;
        const overrideProps = {
            policyContactsRoles: {
                availableValues: policyContactRoleOptions,
                value: policyContactRole
            },
            policyContactsDataTable: {
                key: policyContactRole,
                onFetchData: this.loadData,
                data: this.state.data
            }
        };

        const resolvers = {
            resolveClassNameMap: tabStyles,
            resolveCallbackMap: {
                renderContact: this.renderContact,
                renderRole: this.renderRole,
                renderPoliciesInvolved: this.renderPoliciesInvolved,
                onRowClick: (data, index) => {
                    this.redirectToContactDetails(index);
                }
            }
        };

        const breakpoint = this.getBreakPoint();

        if(breakpoint && breakpoint == 'phone' && policyContactRole != previousContactRole){
            this.loadTableData();
            this.setState({previousContactRole: policyContactRole});
        }

        const _metadata = getMetadataByBreakPoint(metadata, breakpoint);

        const readValue = (id, path) => {
            return readViewModelValue(
                _metadata,
                policyContactRole,
                id,
                path,
                overrideProps
            );
        };

        return (
            <ViewModelForm
                uiProps={_metadata}
                overrideProps={overrideProps}
                onValueChange={this.writeValue}
                resolveValue={readValue}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        );
    }
}

export const PolicyContactsComponent = PolicyContactsPage;
export default withRouter(withViewModelService(withAuthenticationContext(PolicyContactsPage)));
